import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '@/shared/lib/utils';

const buttonGroupVariants = cva('inline-flex', {
  variants: {
    orientation: {
      horizontal: 'flex-row',
      vertical: 'flex-col',
    },
  },
  defaultVariants: {
    orientation: 'horizontal',
  },
});

type ButtonGroupProps = ComponentProps<'div'> &
  VariantProps<typeof buttonGroupVariants> & {
    /** グループのアクセシビリティラベル */
    'aria-label'?: string;
  };

/**
 * 関連するボタンをグループ化するコンテナコンポーネント
 *
 * @example
 * <ButtonGroup>
 *   <Button variant="outline">左</Button>
 *   <Button variant="outline">中央</Button>
 *   <Button variant="outline">右</Button>
 * </ButtonGroup>
 */
export const ButtonGroup = ({
  className,
  orientation,
  children,
  'aria-label': ariaLabel,
  ...props
}: ButtonGroupProps) => {
  return (
    // biome-ignore lint/a11y/useSemanticElements: fieldsetはボタングループに適切でない
    <div
      className={cn(
        buttonGroupVariants({ orientation }),
        // 水平方向: 最初と最後以外の角丸を削除
        orientation !== 'vertical' &&
          '[&>*:first-child]:rounded-r-none [&>*:last-child]:rounded-l-none [&>*:not(:first-child):not(:last-child)]:rounded-none',
        // 垂直方向: 最初と最後以外の角丸を削除
        orientation === 'vertical' &&
          '[&>*:first-child]:rounded-b-none [&>*:last-child]:rounded-t-none [&>*:not(:first-child):not(:last-child)]:rounded-none',
        // ボーダーの重なりを防ぐ
        orientation !== 'vertical' && '[&>*:not(:first-child)]:-ml-px',
        orientation === 'vertical' && '[&>*:not(:first-child)]:-mt-px',
        className,
      )}
      role="group"
      aria-label={ariaLabel}
      {...props}
    >
      {children}
    </div>
  );
};

ButtonGroup.displayName = 'ButtonGroup';
